import { Injectable } from '@angular/core';
import { Dictionary } from 'highcharts';
import { Restangular } from 'ngx-restangular';
import { BaseResponse } from '../../interfaces/base-response-interface';

export interface VendorProduct {
  id: string;
  vendorName?: string;
  productName?: string;
  vendorSlug?: string;
  productSlug?: string;
  stripeProductId?: string;
  stripeVendor?: string;
  metadata: Dictionary<any>;
  feedConfig?: Dictionary<any>;
  createdAt: string;
  modifiedAt: string;
}

export interface VendorProductCompletedTransferLog {
  id: string;
  createdAt: string;
  direction: string;
  metadata: Dictionary<any>;
  type: string;
  vendorProductId: string;
  vendorProductName?: string;
}

export interface VendorProductTransferLog {
  id: string;
  vendorProductName: string;
  metadata: Dictionary<any>;
  data: Dictionary<any>;
  status: string;
  createdAt: string;
}

export type VendorApiCallLogProjectionStatus = 'projected' | 'empty' | 'invalid_shape';
export type VendorApiCallLogOutcome =
  | 'completed'
  | 'disabled'
  | 'failed'
  | 'partial_success'
  | 'pending'
  | 'provider_error'
  | 'queued'
  | 'running'
  | 'success'
  | 'timeout'
  | 'validation_error';
export type VendorApiCallLogErrorCategory =
  | 'authentication_error'
  | 'configuration_error'
  | 'provider_error'
  | 'rate_limited'
  | 'timeout'
  | 'transport_error'
  | 'validation_error';
export type VendorApiCallLogHttpMethod = 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';

export type VendorApiCallLogOperation =
  | 'aflacTierOne.handoff.create'
  | 'aflacTierOne.enrollmentStatus.read'
  | 'albumHealth.register'
  | 'allstate.lead.create'
  | 'federalLife.auth.token'
  | 'federalLife.enrollment.create'
  | 'federalLife.enrollment.update'
  | 'federalLife.enrollment.terminate'
  | 'federalLife.idp.signup'
  | 'federalLife.bankInfo.list'
  | 'federalLife.paymentMethod.card.save'
  | 'federalLife.payment.schedule'
  | 'guardian.policy.enroll'
  | 'guardian.dependents.add'
  | 'guardian.dependent.delete'
  | 'guardian.policy.cancel'
  | 'joya.enrollment.create'
  | 'lemonade.auth.token'
  | 'lemonade.discountCode.create'
  | 'rxValet.member.enroll'
  | 'rxValet.dependent.add'
  | 'rxValet.dependent.update'
  | 'rxValet.member.login.primary'
  | 'rxValet.member.login.dependent'
  | 'rxValet.member.changePlan'
  | 'rxValet.primary.deactivate'
  | 'rxValet.dependent.deactivate'
  | 'rxValet.primary.update'
  | 'sleekDental.auth.validate'
  | 'sleekDental.member.enroll'
  | 'thatch.employers.list'
  | 'thatch.members.list'
  | 'thatch.employees.list'
  | 'thatch.enrollments.list'
  | 'uvp.auth.token'
  | 'uvp.member.createPrimary'
  | 'uvp.member.createDependent'
  | 'uvp.member.updatePrimary'
  | 'uvp.member.deactivatePrimary'
  | 'uvp.member.deactivateDependent'
  | 'uvp.member.rollbackPartialEnrollment'
  | 'carrierIntegrationRerun.queueVendor'
  | 'carrierIntegrationRerun.queueProduct'
  | 'carrierIntegrationRerun.vendorStarted'
  | 'carrierIntegrationRerun.productCompleted'
  | 'carrierIntegrationRerun.vendorCompleted'
  | 'carrierIntegrationPreflight.run';

interface VendorApiCallLogResponseSummaryMetadata {
  schemaVersion: 1;
  outcome: VendorApiCallLogOutcome | null;
  errorCategory: VendorApiCallLogErrorCategory | null;
  httpStatus: number | null;
  durationMs: number | null;
  retryCount: number | null;
  correlationId: string | null;
}

type EmptyProviderProjection = { readonly noProviderFields?: never };

type RequireAtLeastOne<FieldSet> = {
  [Field in keyof FieldSet]-?: Required<Pick<FieldSet, Field>> & Partial<Omit<FieldSet, Field>>;
}[keyof FieldSet];

interface AflacEnrollmentStepFields {
  quote?: 'Yes' | 'No';
  personalDetails?: 'Yes' | 'No';
  payment?: 'Yes' | 'No';
  policySigned?: 'Yes' | 'No';
  policyActivated?: 'Yes' | 'No';
}

interface AflacEnrollmentStatusProviderFields {
  steps?: RequireAtLeastOne<AflacEnrollmentStepFields>;
  effectiveDate?: string;
}

export type AflacEnrollmentStatusProviderProjection =
  RequireAtLeastOne<AflacEnrollmentStatusProviderFields>;

export interface AccountReferenceProviderProjection {
  accountReferencePresent: boolean;
}

interface AllstateLeadProviderFields {
  leadReferencePresent?: boolean;
  launchUrlIssued?: boolean;
}

export type AllstateLeadProviderProjection = RequireAtLeastOne<AllstateLeadProviderFields>;

export interface ViolationCountProviderProjection {
  violationCount: number;
}

interface FederalEnrollmentProviderFields {
  enrollmentReferencePresent?: boolean;
  employeeReferencePresent?: boolean;
  coverageEffectiveDate?: string;
  dependentCount?: number;
}

export type FederalEnrollmentProviderProjection = RequireAtLeastOne<FederalEnrollmentProviderFields>;

export interface FederalBankInfoProviderProjection {
  candidateCount: number;
}

export interface FederalPaymentMethodProviderProjection {
  paymentMethodReferencePresent: boolean;
}

interface GuardianProviderFields {
  primaryCustomerReferencePresent?: boolean;
  policyReferencePresent?: boolean;
  correlationId?: string;
}

export type GuardianProviderProjection = RequireAtLeastOne<GuardianProviderFields>;

export interface JoyaProviderProjection {
  validationErrorCount: number;
}

interface LemonadeDiscountProviderFields {
  status?: 'success';
  requestId?: string;
  launchUrlIssued?: boolean;
}

export type LemonadeDiscountProviderProjection = RequireAtLeastOne<LemonadeDiscountProviderFields>;

interface RxValetMemberProviderFields {
  statusCode?: string;
  primaryMemberReferencePresent?: boolean;
}

export type RxValetMemberProviderProjection = RequireAtLeastOne<RxValetMemberProviderFields>;

interface RxValetDependentProviderFields {
  statusCode?: string;
  dependentReferencePresent?: boolean;
}

export type RxValetDependentProviderProjection = RequireAtLeastOne<RxValetDependentProviderFields>;

export interface RxValetStatusProviderProjection {
  statusCode: string;
}

export interface ItemCountProviderProjection {
  itemCount: number;
}

interface UvpMemberProviderFields {
  memberRecordReferencePresent?: boolean;
  memberIdentifierPresent?: boolean;
}

export type UvpMemberProviderProjection = RequireAtLeastOne<UvpMemberProviderFields>;

export interface CarrierQueueVendorProviderProjection {
  jobId?: string;
  status: 'queued';
  vendorId?: string;
  vendorSlug?: string;
  vendorProductCount?: number;
  queuedAt?: string;
}

export interface CarrierQueueProductProviderProjection extends CarrierQueueVendorProviderProjection {
  vendorProductId?: string;
}

export interface CarrierProductProviderProjection {
  vendorProductId?: string;
  vendorSlug?: string;
  productSlug?: string;
  status?: 'succeeded' | 'failed';
  httpStatus: number | null;
  attemptedRows?: number;
  newUserCount?: number;
  updatedCount?: number;
  errorCount?: number;
  startedAt?: string;
  completedAt?: string;
}

export interface CarrierVendorStartedProviderProjection {
  status: 'running';
  startedAt?: string;
}

export interface CarrierVendorCompletedProviderProjection {
  jobId?: string;
  status: 'completed';
  vendorId?: string;
  vendorSlug?: string;
  totalAttempted?: number;
  totalSucceeded?: number;
  totalFailed?: number;
  queuedAt?: string;
  startedAt?: string;
  completedAt?: string;
  products: CarrierProductProviderProjection[];
  productsOmittedCount?: number;
}

export interface CarrierPreflightProviderProjection {
  status: 'disabled' | 'failed' | 'succeeded';
  startedAt?: string;
  completedAt?: string;
  productCount: number;
  succeededCount: number;
  failedCount: number;
  products: CarrierProductProviderProjection[];
  productsOmittedCount?: number;
  errorCode?:
    | 'CARRIER_INTEGRATION_PREFLIGHT_FAILED'
    | 'CARRIER_INTEGRATION_PREFLIGHT_NO_ELIGIBLE_PRODUCTS';
}

type ProjectedSummary<Operation extends VendorApiCallLogOperation, Provider> =
  VendorApiCallLogResponseSummaryMetadata & {
    operation: Operation;
    projectionStatus: 'projected';
    provider: Provider;
  };

type EmptyProjectionSummary = VendorApiCallLogResponseSummaryMetadata & {
  operation: VendorApiCallLogOperation;
  projectionStatus: 'empty' | 'invalid_shape';
  provider: EmptyProviderProjection;
};

type VendorApiCallLogProjectedResponseSummary =
  | ProjectedSummary<'aflacTierOne.enrollmentStatus.read', AflacEnrollmentStatusProviderProjection>
  | ProjectedSummary<'albumHealth.register' | 'sleekDental.member.enroll', AccountReferenceProviderProjection>
  | ProjectedSummary<'allstate.lead.create', AllstateLeadProviderProjection>
  | ProjectedSummary<
      'federalLife.enrollment.create' | 'federalLife.enrollment.update',
      FederalEnrollmentProviderProjection | ViolationCountProviderProjection
    >
  | ProjectedSummary<
      'federalLife.enrollment.terminate' | 'federalLife.payment.schedule',
      ViolationCountProviderProjection
    >
  | ProjectedSummary<
      'federalLife.bankInfo.list',
      FederalBankInfoProviderProjection | ViolationCountProviderProjection
    >
  | ProjectedSummary<
      'federalLife.paymentMethod.card.save',
      FederalPaymentMethodProviderProjection | ViolationCountProviderProjection
    >
  | ProjectedSummary<
      'guardian.policy.enroll' | 'guardian.dependents.add' | 'guardian.dependent.delete' | 'guardian.policy.cancel',
      GuardianProviderProjection
    >
  | ProjectedSummary<'joya.enrollment.create', JoyaProviderProjection>
  | ProjectedSummary<'lemonade.discountCode.create', LemonadeDiscountProviderProjection>
  | ProjectedSummary<'rxValet.member.enroll', RxValetMemberProviderProjection>
  | ProjectedSummary<'rxValet.dependent.add', RxValetDependentProviderProjection>
  | ProjectedSummary<
      'rxValet.dependent.update' | 'rxValet.member.changePlan' | 'rxValet.primary.deactivate'
      | 'rxValet.dependent.deactivate' | 'rxValet.primary.update',
      RxValetStatusProviderProjection
    >
  | ProjectedSummary<
      'thatch.employers.list' | 'thatch.members.list' | 'thatch.employees.list' | 'thatch.enrollments.list',
      ItemCountProviderProjection
    >
  | ProjectedSummary<'uvp.member.createPrimary' | 'uvp.member.createDependent', UvpMemberProviderProjection>
  | ProjectedSummary<'carrierIntegrationRerun.queueVendor', CarrierQueueVendorProviderProjection>
  | ProjectedSummary<'carrierIntegrationRerun.queueProduct', CarrierQueueProductProviderProjection>
  | ProjectedSummary<'carrierIntegrationRerun.vendorStarted', CarrierVendorStartedProviderProjection>
  | ProjectedSummary<'carrierIntegrationRerun.productCompleted', CarrierProductProviderProjection>
  | ProjectedSummary<'carrierIntegrationRerun.vendorCompleted', CarrierVendorCompletedProviderProjection>
  | ProjectedSummary<'carrierIntegrationPreflight.run', CarrierPreflightProviderProjection>;

export type VendorApiCallLogResponseSummary =
  | EmptyProjectionSummary
  | VendorApiCallLogProjectedResponseSummary;

export type VendorApiCallLogResponse =
  | {
      availability: 'available';
      summary: VendorApiCallLogResponseSummary;
    }
  | {
      availability: 'historical_unavailable';
    };

export interface VendorApiCallLogItem {
  id: string | null;
  createdAt: string | null;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  vendorSlug: string | null;
  vendorProductId: string | null;
  vendorName: string | null;
  productName: string | null;
  httpMethod: VendorApiCallLogHttpMethod | null;
  httpStatus: number | null;
  routeTemplate: string | null;
  requestAvailability: 'not_returned';
  response: VendorApiCallLogResponse;
  headerAvailability: 'not_returned';
}

export interface VendorApiCallLogListResponse {
  items: VendorApiCallLogItem[];
  page: number;
  limit: number;
  total: number;
}

export interface VendorProductCarrierIntegrationRerunResult {
  jobId?: string;
  status?: string;
  vendorId?: string;
  vendorSlug?: string;
  vendorProductId?: string;
  vendorProductCount?: number;
  queuedAt?: string;
}

export interface BrandResolvedVendorProduct {
  id: string;
  vendorName?: string;
  productName?: string;
  vendorSlug?: string;
  productSlug?: string;
  stripeProductId?: string;
}

export interface BrandSourceProduct {
  id: string;
  name?: string;
  title?: string;
  advertiserName?: string;
  vendorProductId?: string;
}

export interface BrandSkippedSourceProduct extends BrandSourceProduct {
  reason: string;
  vendorProducts?: BrandResolvedVendorProduct[];
}

export type BrandSkippedStripeProduct = BrandSkippedSourceProduct;

export interface BrandVendorProductResolution {
  brand: {
    partnerId?: string;
    name?: string;
    shortName?: string;
    redirectUrl?: string;
    slug?: string;
    stripeProductCompany?: string;
  };
  matchedVendorProducts: BrandResolvedVendorProduct[];
  matchedSourceProducts?: BrandSourceProduct[];
  skippedSourceProducts?: BrandSkippedSourceProduct[];
  skippedStripeProducts?: BrandSkippedSourceProduct[];
  duplicateMappings?: Array<{
    stripeProductId?: string;
    vendorProductId?: string;
    vendorProducts?: BrandResolvedVendorProduct[];
    vendorProduct?: BrandResolvedVendorProduct;
    sourceProduct?: BrandSourceProduct;
  }>;
  counts?: {
    sourceProducts?: number;
    matchedVendorProducts: number;
    skippedSourceProducts?: number;
    stripeProducts?: number;
    skippedStripeProducts?: number;
    duplicateMappings: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class VendorProductsService {
  constructor(private restangular: Restangular) {}

  deleteVendorPendingTransferLog(
    vendorPendingTransferId: string,
  ): Promise<VendorProductTransferLog> {
    return this.restangular
      .one('/products/pendingtransfer/', vendorPendingTransferId)
      .customDELETE()
      .toPromise() as Promise<VendorProductTransferLog>;
  }

  getAllVendorProducts(): Promise<Array<VendorProduct>> {
    return this.restangular
      .one('vendors')
      .one('products')
      .one('all?limit=10000')
      .getList()
      .toPromise();
  }

  getBrandAvailableVendorProducts(brandId: string): Promise<BrandVendorProductResolution> {
    return this.restangular
      .one('products')
      .one('brand')
      .one(brandId)
      .one('vendor-products')
      .customGET()
      .toPromise();
  }

  postVendorProduct(vendorProduct: Partial<VendorProduct>): Promise<VendorProduct> {
    return this.restangular.one('vendors').one('products').customPOST(vendorProduct).toPromise();
  }

  getAllVendorProductsV5(): Promise<BaseResponse<Array<VendorProduct>>> {
    return this.restangular
      .one('v5')
      .one('vendors')
      .one('products')
      .one('all')
      .customGET()
      .toPromise();
  }

  getVendorProductById(productId: string): Promise<VendorProduct> {
    return this.restangular
      .one('v5')
      .one('vendors')
      .one('products')
      .one(productId)
      .customGET()
      .toPromise()
      .then((result: { data: VendorProduct }) => result.data);
  }

  putVendorProduct(
    productId: string,
    productData: Partial<VendorProduct>,
  ): Promise<BaseResponse<VendorProduct>> {
    return this.restangular
      .one('v5')
      .one('vendors')
      .one('products')
      .one(productId)
      .customPUT(productData)
      .toPromise();
  }

  deleteVendorProduct(productId: string): Promise<any> {
    return this.restangular.one('vendors').one('products', productId).customDELETE().toPromise();
  }

  runFtp(productId: string, method: 'enroll' | 'payment'): Promise<any> {
    return this.restangular
      .one('vendors')
      .one('products')
      .one('runftp')
      .one(productId)
      .one(method)
      .customPUT({})
      .toPromise();
  }

  rerunCarrierIntegration(productId: string, reason: string): Promise<VendorProductCarrierIntegrationRerunResult> {
    return this.restangular
      .one('vendors')
      .one('products', productId)
      .one('carrier-integration-reruns')
      .customPOST({ reason })
      .toPromise() as Promise<VendorProductCarrierIntegrationRerunResult>;
  }

  getVendorPendingTransferLogs(
    searchValue: string = '',
    page: number = 0,
    limit: number = 25,
  ): Promise<Array<VendorProductTransferLog>> {
    return this.restangular
      .one(`/products/pendingtransfer/all?filter=${searchValue}&page=${page}&limit=${limit}`)
      .getList()
      .toPromise();
  }

  getVendorTransferLogs(
    searchValue: string = '',
    page: number = 0,
    limit: number = 25,
  ): Promise<Array<VendorProductCompletedTransferLog>> {
    return this.restangular
      .one(`/products/transfer/all?filter=${encodeURIComponent(searchValue)}&page=${page}&limit=${limit}`)
      .getList()
      .toPromise();
  }

  getProductWebhookLogs(
    searchValue: string = '',
    page: number = 0,
    limit: number = 25,
  ): Promise<any[]> {
    return this.restangular
      .one(`/products/webhook/all?filter=${searchValue}&page=${page}&limit=${limit}`)
      .getList()
      .toPromise();
  }

  getVendorApiCallLogs(
    searchValue: string = '',
    page: number = 0,
    limit: number = 25,
  ): Promise<VendorApiCallLogListResponse> {
    return this.restangular
      .one(`/products/vendor-api-calls/all?filter=${searchValue}&page=${page}&limit=${limit}`)
      .customGET()
      .toPromise();
  }

  getUniqueVendorProducts(): Promise<Array<VendorProduct>> {
    return this.restangular.one('vendors').one('products').one('unique').getList().toPromise();
  }

  putVendorPendingTransferLogItem(
    vendorPendingTransfer: VendorProductTransferLog,
  ): Promise<VendorProductTransferLog> {
    return this.restangular
      .one('/products/pendingtransfer/', vendorPendingTransfer.id)
      .customPUT(vendorPendingTransfer)
      .toPromise() as Promise<VendorProductTransferLog>;
  }
}
